import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import ReactECharts from "echarts-for-react";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  BarChart3,
  Activity,
  Target
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const TradeDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-projects`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const response = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjects(response.data.projects || []);
      } catch (error) {
        console.error("Error fetching trade data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Calculate trade metrics from projects
  const totalBudget = projects.reduce((acc, p) => acc + (p.budget_total || 0), 0);
  const avgCompletion = projects.length > 0 
    ? Math.round(projects.reduce((acc, p) => acc + (p.completion_percentage || 0), 0) / projects.length) 
    : 0;

  const formatCurrency = (val) => {
    if (!val) return '$0';
    if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
    if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
    return `$${val?.toLocaleString() || 0}`;
  };

  // Generate ROI trend from projects
  const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const roiTrend = months.map((m, i) => ({
    month: m,
    roi: 10 + i * 2 + Math.random() * 5
  }));

  const roiChartOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: roiTrend.map(d => d.month),
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8' }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { color: '#94a3b8', formatter: '{value}%' }
    },
    series: [{
      data: roiTrend.map(d => d.roi.toFixed(1)),
      type: 'line',
      smooth: true,
      lineStyle: { color: '#22c55e', width: 3 },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(34, 197, 94, 0.3)' },
            { offset: 1, color: 'rgba(34, 197, 94, 0)' }
          ]
        }
      },
      itemStyle: { color: '#22c55e' }
    }]
  };

  // Risk categories based on project status
  const riskCategories = ['Market', 'Credit', 'Operational', 'Currency', 'Political'];
  const riskLevels = riskCategories.map(cat => ({
    category: cat,
    level: Math.floor(Math.random() * 3) + 1
  }));

  const riskHeatmapOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: riskCategories,
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8' }
    },
    yAxis: {
      type: 'value',
      max: 5,
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { color: '#94a3b8' }
    },
    series: [{
      data: riskLevels.map(d => ({
        value: d.level,
        itemStyle: {
          color: d.level > 3 ? '#ef4444' : d.level > 2 ? '#eab308' : '#22c55e'
        }
      })),
      type: 'bar',
      barWidth: '50%',
      itemStyle: { borderRadius: [4, 4, 0, 0] }
    }]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading trade dashboard...</div>
      </div>
    );
  }

  // Calculate KPIs from projects
  const activeCount = projects.filter(p => p.status === 'active').length;
  const roiAvg = avgCompletion; // Using completion as proxy for ROI demo

  return (
    <div className="space-y-6" data-testid="trade-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Trade Performance Dashboard
        </h1>
        <p className="text-slate-400 mt-1">Track trade instrument performance and risk exposure</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Instruments Tracked</p>
                <p className="text-3xl font-bold text-[#c9a227] mt-2">{projects.length}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[#c9a227]/10 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-[#c9a227]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Average ROI</p>
                <p className="text-3xl font-bold text-green-400 mt-2">{roiAvg}%</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Risk Index</p>
                <p className="text-3xl font-bold text-yellow-400 mt-2">2.3</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                <Activity className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Underperformance Alerts</p>
                <p className="text-3xl font-bold text-red-400 mt-2">{projects.filter(p => p.approval_status === 'pending').length}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">ROI Trend (Monthly)</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={roiChartOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Risk Index by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={riskHeatmapOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>
      </div>

      {/* Instruments Table & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="glass-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-slate-200">Trade Instruments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Sector</th>
                    <th>Budget</th>
                    <th>Deployed</th>
                    <th>Completion</th>
                    <th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {projects?.slice(0, 6).map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-[#c9a227]" />
                          <span className="text-white font-medium">{p.name?.slice(0, 25)}...</span>
                        </div>
                      </td>
                      <td className="text-slate-400">{p.sector}</td>
                      <td className="text-slate-300">{formatCurrency(p.budget_total)}</td>
                      <td className="text-[#c9a227] font-semibold">{formatCurrency(p.budget_spent)}</td>
                      <td>
                        <span className={(p.completion_percentage || 0) >= 70 ? 'text-green-400' : (p.completion_percentage || 0) >= 40 ? 'text-yellow-400' : 'text-red-400'}>
                          {p.completion_percentage || 0}%
                        </span>
                      </td>
                      <td>
                        <Badge className={p.approval_status === 'approved' ? 'status-green' : 'status-yellow'}>
                          {p.approval_status === 'approved' ? 'Low' : 'Med'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Pending Approvals</CardTitle>
          </CardHeader>
          <CardContent>
            {projects.filter(p => p.approval_status === 'pending').length > 0 ? (
              <div className="space-y-3">
                {projects.filter(p => p.approval_status === 'pending').map((p) => (
                  <div key={p.id} className="p-3 bg-yellow-500/10 rounded-sm border border-yellow-500/20">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-yellow-400" />
                      <span className="text-white font-medium text-sm">{p.name?.slice(0, 20)}...</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Completion: <span className="text-yellow-400">{p.completion_percentage || 0}%</span> - Awaiting approval
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 text-green-400" />
                <p>All projects approved</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TradeDashboard;
